/**
 * components/PlaybookBuilderContainer.tsx
 * Main orchestrator component for playbook builder
 *
 * Manages playbook list display, modal state, and coordinates all playbook operations.
 * Follows the FeeConfigurationContainer pattern.
 */

// 1. External dependencies
import { useState, useCallback, useImperativeHandle, forwardRef } from 'react';
import { Plus, BookOpen, Info } from 'lucide-react';

// 2. Internal absolute imports
import { Card, CardHeader, CardBody, CardFooter, Button, Accordion, AccordionItem } from '@enfomentis/ui';

// 3. Relative imports
import { usePlaybookBuilder } from '../hooks/usePlaybookBuilder';
import type { PlaybookConfluenceLinkDraft } from '../hooks/usePlaybookBuilder';
import { PlaybookAccordion } from './PlaybookAccordion';
import { PlaybookEditModal } from '../modals/PlaybookEditModal';
import { CriteriaItemPicker } from '../modals/CriteriaItemPicker';
import { ConfluencePicker } from '../modals/ConfluencePicker';
import { EntryModelPicker } from '../modals/EntryModelPicker';

/**
 * Modal state management
 */
interface ModalState {
  isOpen: boolean;
  mode: 'add' | 'edit';
  selectedPlaybookId?: string;
}

/**
 * Exposed methods via ref
 */
export interface PlaybookBuilderContainerRef {
  flashInvalidPlaybooks: () => void;
}

/**
 * Playbook builder container component
 */
export const PlaybookBuilderContainer = forwardRef<PlaybookBuilderContainerRef>((_props, ref) => {
  const [state, operations] = usePlaybookBuilder();
  const { playbooks, criteriaItems, marketConditionCategories, confluences, entryModels, entryModelCategories } = state;

  // Modal state
  const [modalState, setModalState] = useState<ModalState>({
    isOpen: false,
    mode: 'add'
  });

  // Highlight state for validation feedback
  const [highlightedPlaybookIds, setHighlightedPlaybookIds] = useState<Set<string>>(new Set());

  /**
   * Flash highlight for playbooks missing entry models
   */
  const flashInvalidPlaybooks = useCallback((): void => {
    // Find playbooks without entry models
    const invalidIds = playbooks
      .filter(p => !p.entryModelIds || p.entryModelIds.length === 0)
      .map(p => p.tempId);

    if (invalidIds.length > 0) {
      setHighlightedPlaybookIds(new Set(invalidIds));

      // Clear highlight after 2 seconds
      setTimeout(() => {
        setHighlightedPlaybookIds(new Set());
      }, 2000);
    }
  }, [playbooks]);

  /**
   * Expose methods via ref
   */
  useImperativeHandle(ref, () => ({
    flashInvalidPlaybooks
  }), [flashInvalidPlaybooks]);

  // Criteria picker state
  const [criteriaPickerState, setCriteriaPickerState] = useState({
    isOpen: false,
    playbookId: ''
  });

  // Confluence picker state
  const [confluencePickerState, setConfluencePickerState] = useState({
    isOpen: false,
    playbookId: ''
  });

  // Entry model picker state
  const [entryModelPickerState, setEntryModelPickerState] = useState({
    isOpen: false,
    playbookId: ''
  });

  /**
   * Open modal for adding a new playbook
   */
  const handleAddPlaybook = useCallback((): void => {
    setModalState({
      isOpen: true,
      mode: 'add'
    });
  }, []);

  /**
   * Open modal for editing an existing playbook
   */
  const handleEditPlaybook = useCallback((tempId: string): void => {
    setModalState({
      isOpen: true,
      mode: 'edit',
      selectedPlaybookId: tempId
    });
  }, []);

  /**
   * Close modal
   */
  const handleCloseModal = useCallback((): void => {
    setModalState({
      isOpen: false,
      mode: 'add'
    });
  }, []);

  /**
   * Save playbook (add or update)
   */
  const handleSavePlaybook = useCallback((
    data: { name: string; description?: string; philosophy?: string }
  ): void => {
    if (modalState.mode === 'add') {
      operations.addPlaybook(data.name, data.description, data.philosophy);
    } else if (modalState.mode === 'edit' && modalState.selectedPlaybookId) {
      operations.updatePlaybook(modalState.selectedPlaybookId, data);
    }

    handleCloseModal();
  }, [modalState, operations, handleCloseModal]);

  /**
   * Handle adding new criteria item with optional new category
   */
  const handleAddCriteriaItem = useCallback((
    criteriaData: { name: string; description?: string; categoryId?: string },
    newCategory?: { name: string; description?: string; color: string }
  ): string => {
     // Create category first if provided
    let categoryId = criteriaData.categoryId;
    if (newCategory) {
      categoryId = operations.addMarketConditionCategory(
        newCategory.name,
        newCategory.color,
        newCategory.description
      );
    }

    // Create criteria item with the category ID
    return operations.addCriteriaItem(
      criteriaData.name,
      criteriaData.description,
      categoryId
    );
  }, [operations]);

  /**
   * Handle adding new market condition category
   */
  const handleAddMarketConditionCategory = useCallback((
    name: string,
    color: string,
    description?: string
  ): string => {
    return operations.addMarketConditionCategory(name, color, description);
  }, [operations]);

  /**
   * Handle adding new entry model with optional new category
   */
  const handleAddEntryModel = useCallback((
    entryModelData: { name: string; description?: string; categoryId?: string },
    newCategory?: { name: string; description?: string; color: string }
  ): string => {
    // Create category first if provided
    let categoryId = entryModelData.categoryId;
    if (newCategory) {
      categoryId = operations.addEntryModelCategory(
        newCategory.name,
        newCategory.color,
        newCategory.description
      );
    }

    // Create entry model with the category ID
    return operations.addEntryModel(
      entryModelData.name,
      entryModelData.description,
      categoryId
    );
  }, [operations]);

  /**
   * Handle adding new entry model category
   */
  const handleAddEntryModelCategory = useCallback((
    name: string,
    color: string,
    description?: string
  ): string => {
    return operations.addEntryModelCategory(name, color, description);
  }, [operations]);

  /**
   * Open criteria picker for a specific playbook
   */
  const handleAddCriteria = useCallback((playbookId: string): void => {
    setCriteriaPickerState({
      isOpen: true,
      playbookId
    });
  }, []);

  /**
   * Close criteria picker
   */
  const handleCloseCriteriaPicker = useCallback((): void => {
    setCriteriaPickerState({
      isOpen: false,
      playbookId: ''
    });
  }, []);

  /**
   * Handle criteria selection change
   */
  const handleCriteriaSelectionChange = useCallback((selectedIds: string[]): void => {
    if (criteriaPickerState.playbookId) {
      operations.updatePlaybook(criteriaPickerState.playbookId, {
        criteriaIds: selectedIds
      });
    }
  }, [criteriaPickerState.playbookId, operations]);

  /**
   * Open confluence picker for a specific playbook
   */
  const handleAddConfluences = useCallback((playbookId: string): void => {
    setConfluencePickerState({
      isOpen: true,
      playbookId
    });
  }, []);

  /**
   * Close confluence picker
   */
  const handleCloseConfluencePicker = useCallback((): void => {
    setConfluencePickerState({
      isOpen: false,
      playbookId: ''
    });
  }, []);

  /**
   * Handle confluence selection change (receives confluence links with polarity)
   */
  const handleConfluenceSelectionChange = useCallback((selectedConfluences: PlaybookConfluenceLinkDraft[]): void => {
    if (confluencePickerState.playbookId) {
      operations.updatePlaybook(confluencePickerState.playbookId, {
        confluences: selectedConfluences
      });
    }
  }, [confluencePickerState.playbookId, operations]);

  /**
   * Open entry model picker for a specific playbook
   */
  const handleAddEntryModels = useCallback((playbookId: string): void => {
    setEntryModelPickerState({
      isOpen: true,
      playbookId
    });
  }, []);

  /**
   * Close entry model picker
   */
  const handleCloseEntryModelPicker = useCallback((): void => {
    setEntryModelPickerState({
      isOpen: false,
      playbookId: ''
    });
  }, []);

  /**
   * Handle entry model selection change
   */
  const handleEntryModelSelectionChange = useCallback((selectedIds: string[]): void => {
    if (entryModelPickerState.playbookId) {
      operations.updatePlaybook(entryModelPickerState.playbookId, {
        entryModelIds: selectedIds
      });
    }
  }, [entryModelPickerState.playbookId, operations]);

  /**
   * Delete playbook
   */
  const handleDeletePlaybook = useCallback((tempId: string): void => {
    operations.removePlaybook(tempId);
  }, [operations]);

  // Get existing playbook data for edit mode
  const existingPlaybook = modalState.selectedPlaybookId
    ? operations.getPlaybook(modalState.selectedPlaybookId)
    : undefined;

  // Get current playbook for criteria picker
  const currentPlaybook = criteriaPickerState.playbookId
    ? operations.getPlaybook(criteriaPickerState.playbookId)
    : undefined;

  // Get current playbook for confluence picker
  const currentPlaybookForConfluences = confluencePickerState.playbookId
    ? operations.getPlaybook(confluencePickerState.playbookId)
    : undefined;

  // Get current playbook for entry model picker
  const currentPlaybookForEntryModels = entryModelPickerState.playbookId
    ? operations.getPlaybook(entryModelPickerState.playbookId)
    : undefined;

  return (
    <div className="playbook-builder-container h-[calc(100dvh-13rem)] max-h-[calc(100dvh-12rem)] flex flex-col">
      {/* Main card */}
      <Card
        radius="none"
        shadow="none"
        className="flex flex-col flex-1 min-h-0 w-full h-full"
      >
        <CardHeader className="flex flex-row items-center justify-between shrink-0 border-b border-default-200">
          <div className="flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-primary" />
            <div>
              <h3 className="text-lg font-semibold text-text-primary">Playbooks</h3>
              <p className="hidden sm:block text-sm text-text-secondary">Define your trading playbooks</p>
            </div>
          </div>
          <Button
            color="primary"
            startContent={<Plus className="w-4 h-4" />}
            onPress={handleAddPlaybook}
          >
            Add Playbook
          </Button>
        </CardHeader>

        <CardBody className="flex-1 overflow-y-auto p-4 min-h-0">
          <PlaybookAccordion
            playbooks={playbooks}
            criteriaItems={criteriaItems}
            marketConditionCategories={marketConditionCategories}
            confluences={confluences}
            entryModels={entryModels}
            onEdit={handleEditPlaybook}
            onDelete={handleDeletePlaybook}
            onAddCriteria={handleAddCriteria}
            onAddConfluences={handleAddConfluences}
            onAddEntryModels={handleAddEntryModels}
            highlightedPlaybookIds={highlightedPlaybookIds}
          />
        </CardBody>

        <CardFooter className="shrink-0 border-t border-default-200">
          <Accordion variant="light" fullWidth isCompact>
            <AccordionItem
              key="why-playbooks"
              textValue="Why create playbooks?"
              title={
                <div className="flex items-center gap-2">
                  <Info size={16} className="text-text-secondary" />
                  <span className="text-sm font-medium text-text-secondary">
                    Why create playbooks?
                  </span>
                </div>
              }
            >
              <ul className="text-sm text-text-secondary space-y-1 pb-2">
                <li>• Playbooks define specific trade setups within your strategy</li>
                <li>• Each playbook combines criteria (must-have conditions), confluences (factors affecting confidence), and entry models (execution methods)</li>
                <li>• Multiple playbooks help you track which setups perform best in different conditions</li>
                <li>• You'll be able to tag each trade with the playbook used for detailed performance analysis</li>
              </ul>
            </AccordionItem>
          </Accordion>
        </CardFooter>
      </Card>

      {/* Playbook Edit Modal */}
      <PlaybookEditModal
        isOpen={modalState.isOpen}
        mode={modalState.mode}
        existingData={existingPlaybook}
        onClose={handleCloseModal}
        onSave={handleSavePlaybook}
      />

      {/* Criteria Picker Modal */}
      <CriteriaItemPicker
        isOpen={criteriaPickerState.isOpen}
        onClose={handleCloseCriteriaPicker}
        criteriaItems={criteriaItems}
        marketConditionCategories={marketConditionCategories}
        selectedCriteriaIds={currentPlaybook?.criteriaIds || []}
        playbooks={playbooks}
        currentPlaybookId={criteriaPickerState.playbookId}
        onSelectionChange={handleCriteriaSelectionChange}
        onAddCriteriaItem={handleAddCriteriaItem}
        onCreateCategory={handleAddMarketConditionCategory}
        onUpdateCriteriaItem={operations.updateCriteriaItem}
        onRemoveCriteriaItem={operations.removeCriteriaItem}
      />

      {/* Confluence Picker Modal */}
      <ConfluencePicker
        isOpen={confluencePickerState.isOpen}
        onClose={handleCloseConfluencePicker}
        confluences={confluences}
        marketConditionCategories={marketConditionCategories}
        selectedConfluences={currentPlaybookForConfluences?.confluences || []}
        playbooks={playbooks}
        currentPlaybookId={confluencePickerState.playbookId}
        onSelectionChange={handleConfluenceSelectionChange}
        onAddConfluence={(data) => operations.addConfluence(data.name, data.description, data.categoryId)}
        onCreateCategory={handleAddMarketConditionCategory}
        onUpdateConfluence={operations.updateConfluence}
        onRemoveConfluence={operations.removeConfluence}
      />

      {/* Entry Model Picker Modal */}
      <EntryModelPicker
        isOpen={entryModelPickerState.isOpen}
        onClose={handleCloseEntryModelPicker}
        entryModels={entryModels}
        entryModelCategories={entryModelCategories}
        selectedEntryModelIds={currentPlaybookForEntryModels?.entryModelIds || []}
        playbooks={playbooks}
        currentPlaybookId={entryModelPickerState.playbookId}
        onSelectionChange={handleEntryModelSelectionChange}
        onAddEntryModel={handleAddEntryModel}
        onCreateCategory={handleAddEntryModelCategory}
        onUpdateEntryModel={operations.updateEntryModel}
        onRemoveEntryModel={operations.removeEntryModel}
      />
    </div>
  );
});

PlaybookBuilderContainer.displayName = 'PlaybookBuilderContainer';
